<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import type { ReferencesProps } from "./ReferencesProps.js";
	import translations from "./References.translations.json";
	import { getTranslate } from "../utils/getTranslate.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import DynamicPicture from "../../../shared/components/Picture/DynamicPicture.svelte";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import Link from "../../../shared/components/Link.svelte";
	import arrowRightLong from "../../../../core/assets/icons/arrow-right-long.svg?raw";
	import { textLengthLimit } from "../../../../core/utils/textLengthLimit.js";
	import { categoryNameMap } from "../../../../core/schema/Category.js";
	import { referenceScopeLabels, referenceUseCaseLabels } from "../../../../core/schema/referenceLabels.js";
	import { getReferenceDetailPath } from "../../../../core/schema/paths/getReferenceDetailPath.js";
	import Pill from "../../../shared/components/Pill.svelte";

	type $$Props = ReferencesProps;

	const { references, referenceCategoryMap } = $$restProps as $$Props;

	const { TENANT_LOCALE } = getGlobalSettings();

	const translate = getTranslate(translations);
</script>

<Layout>
	<div class="container">
		{#each references as reference, index}
			{@const scopeLabels = reference.scopeLabels.map((label) => {
				const labelInMap = referenceScopeLabels[label];
				if (labelInMap) {
					return labelInMap[TENANT_LOCALE];
				}
				return label;
			})}
			{@const useCaseLabels = reference.useCaseLabels.map((label) => {
				const labelInMap = referenceUseCaseLabels[label];
				if (labelInMap) {
					return labelInMap[TENANT_LOCALE];
				}
				return label;
			})}
			{@const solutionLabels = referenceCategoryMap[reference.id] ?? []}
			<section
				class="flex flex-col gap-5 lg:gap-8 lg:p-8 xl:flex-row"
				class:xl:!flex-row-reverse={index % 2 !== 0}
				class:lg:mt-8={index === 0}
			>
				{#if reference.previewImage}
					<a href="/{getReferenceDetailPath(reference)}" class="overflow-hidden rounded-lg lg:h-full xl:w-1/2">
						<DynamicPicture
							image={reference.previewImage}
							alt={reference.title}
							width={0}
							height={520}
							loading={index <= 1 ? "eager" : "lazy"}
							imgClass="rounded-lg h-full w-full object-cover inset-0 max-w-[initial] absolute"
							class="relative h-full min-h-[17.5rem] w-full items-center justify-center overflow-hidden lg:min-h-[20rem] xl:flex xl:h-[32.5rem]"
							bucket="storage"
							sourcesets={{
								"<sm": [600, 280],
								">sm": [704, 280],
								">lg": [896, 320],
								">xl": [662, 520],
							}}
						/>
					</a>
				{/if}
				<div
					class="flex flex-col items-start justify-center gap-2 md:gap-4 xl:h-[32.5rem] xl:w-1/2 xl:pl-8 xl:pr-[7rem]"
				>
					{#if solutionLabels.length > 0 || scopeLabels.length > 0 || useCaseLabels.length > 0}
						<div class="flex flex-wrap gap-0.5 md:w-1/2 lg:w-48">
							{#each solutionLabels as solution}
								<Pill>
									{nbspify(categoryNameMap[solution][TENANT_LOCALE])}
								</Pill>
							{/each}
							{#each scopeLabels as scope}
								<Pill>
									{nbspify(scope)}
								</Pill>
							{/each}
							{#each useCaseLabels as useCase}
								<Pill>
									{nbspify(useCase)}
								</Pill>
							{/each}
						</div>
					{/if}
					{#if index === 0}
						<h1 class="m-0 mb-2 p-0 text-lg font-normal leading-none xl:text-2xl">
							{nbspify(reference.title)}
						</h1>
					{:else}
						<h2 class="m-0 mb-2 p-0 text-lg font-normal leading-none xl:text-xl">
							{nbspify(reference.title)}
						</h2>
					{/if}
					<p class="text-2xs mb-2 max-w-[32.5rem] leading-5 lg:mb-5">
						{nbspify(textLengthLimit(reference.perex))}
					</p>
					<div class="flex w-full flex-col gap-2 max-lg:mb-16 md:mt-4 lg:w-[110%] lg:flex-row">
						<Link
							asButton
							variant="primary"
							icon={arrowRightLong}
							href="/{getReferenceDetailPath(reference)}"
							class="justify-between lg:w-auto lg:justify-normal"
						>
							{translate("continueReading")}
						</Link>
					</div>
				</div>
			</section>
		{/each}
	</div>
</Layout>
